import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { HostBottomNav } from '../../components/HostBottomNav';
import NotificationBellButton from '../../components/NotificationBellButton';
import { hostCardStyle } from '../../components/HostSettingsCard';
import { approveInvite, declineInvite, getInvite } from '../../utils/inviteApi';
import { formatEuropeanDate } from '../../utils/dateFormat';

export default function HostPendingApprovalPage() {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [decisionStatus, setDecisionStatus] = useState('pending');

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const res = await getInvite(inviteId);
        setInvite(res);
      } catch (err) {
        toast.error('Invite not found');
      } finally {
        setLoading(false);
      }
    };
    fetchInvite();
  }, [inviteId]);

  const handleApprove = async () => {
    try {
      await approveInvite(inviteId);
      toast.success('Invite approved');
      setDecisionStatus('approved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleDecline = async () => {
    try {
      await declineInvite(inviteId);
      toast.success('Invite declined');
      setDecisionStatus('declined');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline');
    }
  };

  const isResultView = decisionStatus !== 'pending';
  const listingImage = invite?.listing_image || '/allure-logo-mono.png';

  return (
    <div className="app-shell premium-page" data-testid="host-pending-approval-page" style={{ backgroundColor: 'var(--app-bg)' }}>
      <div style={{ padding: '16px 20px 0', display: 'flex', justifyContent: 'flex-end' }}>
        <NotificationBellButton buttonTestId="host-pending-bell-button" popupTestId="host-pending-notification-popup" />
      </div>

      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 0 }}>
        <img src="/allure-logo.png" alt="Allure" style={{ width: 118, height: 102, objectFit: 'contain' }} />
        {isResultView ? (
          <div style={{ width: 120, height: 68, borderRadius: 9999, background: decisionStatus === 'approved' ? '#11B539' : '#FF4A4A', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 18 }}>
            <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 56, fontWeight: 700, color: '#FFFFFF', lineHeight: 1 }}>{decisionStatus === 'approved' ? '✓' : '✕'}</span>
          </div>
        ) : null}
        <h1 style={{ fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700, color: 'var(--text)', marginTop: 12 }}>{decisionStatus === 'approved' ? 'Request Approved' : decisionStatus === 'declined' ? 'Request Declined' : 'Pending Approval'}</h1>

        {loading ? <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, color: 'var(--text-3)', marginTop: 20 }}>Loading request...</p> : null}
        {invite ? (
          isResultView ? (
            <>
              <div style={{ ...hostCardStyle, width: '100%', marginTop: 22, background: 'var(--surface)', borderRadius: 22, padding: '16px 18px' }}>
                <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: 0, lineHeight: 1.28 }}>{decisionStatus === 'approved' ? 'Your guest now has access to Allure. This listing is reserved for those dates and you can follow the active access anytime from the Active QR codes section.' : 'The request has been declined. The guest can try again with the correct stay details and a new invite link will be required.'}</p>
              </div>
              <button onClick={() => navigate(decisionStatus === 'approved' ? '/host/active-qr' : '/host/generate-qr')} data-testid="host-pending-result-button" className="premium-pill-button" style={{ marginTop: 22, width: '100%', maxWidth: 260, padding: '12px 0', fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, cursor: 'pointer' }}>{decisionStatus === 'approved' ? 'Done' : 'Retry'}</button>
            </>
          ) : (
            <>
              <div style={{ ...hostCardStyle, width: '100%', marginTop: 22, padding: '10px 10px 16px' }}>
                <img src={listingImage} alt={invite.listing_name} data-testid="host-pending-listing-image" style={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 18 }} />
                <p data-testid="host-pending-listing-name" style={{ fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: '14px 0 6px', textAlign: 'center' }}>{invite.listing_name}</p>
                <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, fontWeight: 700, color: 'var(--text-2)', margin: '10px 0 0', textAlign: 'center' }}>Guest</p>
                <p data-testid="host-pending-guest-name" style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, color: 'var(--text)', margin: '4px 0 0', textAlign: 'center' }}>{invite.guest_name}</p>
                <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, fontWeight: 700, color: 'var(--text-2)', margin: '16px 0 0', textAlign: 'center' }}>Dates</p>
                <p data-testid="host-pending-dates" style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, color: 'var(--text)', margin: '4px 0 0', textAlign: 'center' }}>{formatEuropeanDate(invite.check_in_date)} - {formatEuropeanDate(invite.checkout_date)}</p>
              </div>

              <button onClick={handleApprove} data-testid="host-pending-approve-button" className="premium-pill-button" style={{ marginTop: 22, width: '100%', maxWidth: 250, padding: '13px 0', fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, cursor: 'pointer' }}>Approve</button>
              <button onClick={handleDecline} data-testid="host-pending-decline-button" className="premium-pill-button" style={{ marginTop: 14, width: '100%', maxWidth: 220, padding: '12px 0', fontFamily: "'Inria Serif', serif", fontSize: 17, fontWeight: 700, cursor: 'pointer' }}>Decline</button>
            </>
          )
        ) : null}
      </div>

      <HostBottomNav />
    </div>
  );
}